import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import { heroImage } from "../data/site";
import { regions, serviceAreas, distanceLabel, kindLabel } from "../data/serviceAreas";
import ServiceAreaMap from "../components/ServiceAreaMap";

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ItemList",
      name: "Pepla Roofing service areas in Maricopa County, AZ",
      itemListElement: serviceAreas.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `${a.name}, AZ`,
        url: `${SITE_URL}/service-areas/${a.slug}`,
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE_URL}/service-areas` },
      ],
    },
  ],
};

const ServiceAreas = () => (
  <div>
    <SEO
      title="Service Areas | Maricopa County, AZ | Pepla Roofing"
      description="We serve cities and towns across Maricopa County, AZ: Phoenix, Glendale, Peoria, Surprise, Scottsdale, Mesa, Chandler and more. Free roof inspections."
      jsonLd={jsonLd}
    />
    <PageHero
      eyebrow="Where We Work"
      title="Service Areas"
      subtitle="Phoenix based, serving cities and towns across Maricopa County."
      image={heroImage}
    />

    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="kicker">Maricopa County, Arizona</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Find Your City</h2>
          <div className="brand-line mx-auto mb-5" />
          <p className="text-muted-foreground text-lg">
            Roof repair, replacement and inspections — from Phoenix to every corner of the Valley.
          </p>
        </div>

        <div className="mb-16">
          <ServiceAreaMap />
        </div>

        <div className="space-y-14">
          {regions.map((r) => {
            const areas = serviceAreas.filter((a) => a.region === r.key);
            if (!areas.length) return null;
            return (
              <div key={r.key}>
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-5 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand" /> {r.name}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {areas.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/service-areas/${a.slug}`}
                      className="group card-lift bg-card border border-ink/10 rounded-2xl p-5 flex items-start gap-4"
                    >
                      <span className="w-11 h-11 rounded-xl bg-ink flex items-center justify-center shrink-0">
                        <MapPin size={20} className="text-brand" />
                      </span>
                      <div className="flex-1">
                        <div className="font-heading text-xl uppercase tracking-wide group-hover:text-brand transition-colors">{a.name}</div>
                        <div className="text-muted-foreground text-xs mt-0.5">{kindLabel(a)} · {distanceLabel(a)}</div>
                      </div>
                      <ArrowRight size={16} className="text-brand mt-1.5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-muted-foreground text-xs text-center mt-12">Distances are approximate driving miles from Phoenix.</p>
      </div>
    </section>

    <section className="py-20 section-dark text-white text-center">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Don't See Your Area?</h2>
        <p className="text-white/70 mb-8">If you're near Maricopa County, give us a call. We'll let you know if we can take on your project.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
          Contact Us <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </div>
);

export default ServiceAreas;
